import React, { useMemo, useRef, useState } from "react";
import { Grid } from "@mantine/core";
import Text from "@/components/atoms/Text";
import { SettingsFormContent } from "@/components/pages/Settings";
import CTA from "@/components/atoms/CTA";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import Space from "@/components/atoms/Space";
import { BankAccount } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import Group from "@/components/atoms/Group";
import SettingsTable from "@/components/molecules/SettingsTable/SettingsTable";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteBankAccount, postNewBankAccount, updateBankAccount } from "@/services/Settings";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import { Controller, useForm } from "react-hook-form";
import {
  BankAccountFormInput,
  BankAccountValidationSchema,
} from "@/components/pages/Settings/validation/bankAccountValidationSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import TextInput from "@/components/atoms/TextInput";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";

interface BankAccountFormProps {
  bankAccounts: BankAccount[];
  currentCompanyId: number | undefined;
  isAdmin: boolean;
}

const BankAccountTable = (props: BankAccountFormProps) => {
  const [isNewBankAccount, setIsNewBankAccount] = useState(false);
  const [isAddEditBankAccountOpen, setIsAddEditBankAccountOpen] = useState(false);
  const [bankAccount, setBankAccount] = useState<BankAccount | undefined>(undefined);
  const { bankAccounts, isAdmin, currentCompanyId } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync: mutateDeleteBankAccount } = useMutation({
    mutationFn: async () => {
      if (!bankAccount) {
        console.error("Error: Address to delete is missing.");
        return;
      }
      await deleteBankAccount(currentCompanyId as number, bankAccount?.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyById", currentCompanyId],
      });
    },
    onError: (error) => {
      console.error("Error deleting bank account:", error);
      notifications.show(ErrorNotificationObj(t("common:genericErrorRetryLater"), ""));
    },
  });

  const columns: ColumnDef<BankAccount>[] = useMemo(
    () => [
      {
        header: t("settings:bankAccountName"),
        accessorKey: "name",
      },
      {
        header: t("settings:bankAccountNr"),
        accessorKey: "accountNo",
      },
      {
        header: "IBAN",
        accessorKey: "iban",
      },
    ],
    [t]
  );

  const editActions = (bankAccount: BankAccount) => [
    {
      label: t("settings:edit"),
      action: () => {
        setIsNewBankAccount(false);
        setIsAddEditBankAccountOpen(true);
        setBankAccount(bankAccount);
      },
    },
    {
      label: t("settings:delete"),
      action: async () => {
        setBankAccount(bankAccount);
        await mutateDeleteBankAccount();
      },
    },
  ];

  return (
    <React.Fragment>
      <SettingsFormContent>
        <Group justify="space-between">
          <Text size="md" strong={true} display="technical" tt="uppercase">
            {t("settings:bankDetails")}
          </Text>
          <CTA
            disabled={!isAdmin}
            id="add-konto"
            intent="secondary"
            onClick={() => {
              setIsNewBankAccount(true);
              setIsAddEditBankAccountOpen(true);
              setBankAccount(undefined);
            }}
          >
            {t("settings:newAccount")}
          </CTA>
        </Group>
        <Space height="25px" />
        <SettingsTable data={bankAccounts} columns={columns} editActions={editActions} isAdmin={isAdmin} />
      </SettingsFormContent>
      <EditOrAddBankAccountModal
        isOpen={isAddEditBankAccountOpen}
        setIsOpen={setIsAddEditBankAccountOpen}
        isNewBankAccount={isNewBankAccount}
        bankAccount={bankAccount}
        companyId={currentCompanyId}
      />
    </React.Fragment>
  );
};

export default BankAccountTable;

interface EditOrAddBankAccountProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isNewBankAccount: boolean;
  bankAccount?: BankAccount;
  companyId: number | undefined;
}

const EditOrAddBankAccountModal = (props: EditOrAddBankAccountProps) => {
  const { isOpen, setIsOpen, isNewBankAccount, bankAccount, companyId } = props;
  const queryClient = useQueryClient();
  const ref = useRef<HTMLFormElement>(null);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
  } = useForm<BankAccountFormInput>({
    resolver: zodResolver(BankAccountValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: {
      name: bankAccount?.name || "",
      accountNo: bankAccount?.accountNo || "",
      iban: bankAccount?.iban || "",
    },
  });

  const { mutateAsync: editBankAccount, isPending: isEditingBankAccount } = useMutation({
    mutationFn: async ({ name, accountNo, iban }: BankAccountFormInput) => {
      if (!bankAccount) {
        console.error("Error: Bank account or its ID is missing or invalid.");
        return;
      }
      await updateBankAccount(companyId as number, bankAccount?.id, name, accountNo.trim(), iban.trim());
    },
    onSuccess: async () => {
      reset();
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyById", companyId],
      });
      setIsOpen(false);
    },
    onError: (error) => {
      console.error("Error updating bank account information:", error);
      notifications.show(ErrorNotificationObj(t("common:genericErrorRetryLater"), ""));
    },
  });

  const { mutateAsync: createBankAccount, isPending: isCreatingBankAccount } = useMutation({
    mutationFn: async ({ name, accountNo, iban }: BankAccountFormInput) => {
      await postNewBankAccount(companyId as number, name, accountNo.trim(), iban.trim());
    },
    onSuccess: async () => {
      reset();
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyById", companyId],
      });
      setIsOpen(false);
    },
    onError: (error) => {
      console.error("Error creating bank account", error);
      notifications.show(ErrorNotificationObj(t("common:genericErrorRetryLater"), ""));
    },
  });

  const onSubmit = async (data: BankAccountFormInput) => {
    if (isNewBankAccount) {
      await createBankAccount({
        name: data.name,
        accountNo: data.accountNo,
        iban: data.iban,
      });
    } else {
      if (!props.bankAccount) {
        console.error("Error: Bank account or its ID is missing or invalid.");
        return;
      }

      await editBankAccount({
        name: data.name,
        accountNo: data.accountNo,
        iban: data.iban,
      });
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={() => setIsOpen(!isOpen)}
      title={isNewBankAccount ? t("settings:addBankAcc") : t("settings:editBankAcc")}
      size="lg"
    >
      <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
        <Grid p={20}>
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label={t("settings:bankAccountName")}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Controller
              control={control}
              name="accountNo"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label={t("settings:bankAccountNr")}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Controller
              control={control}
              name="iban"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label={"IBAN"}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
        </Grid>

        <ModalCTAs>
          <CTA id="add-bankAccount-cancel" intent="secondary" onClick={() => setIsOpen(!isOpen)}>
            {t("actions:cancel")}
          </CTA>
          <CTA
            id="add-bankAccount-save"
            intent="primary"
            type="submit"
            disabled={!isValid}
            loading={isEditingBankAccount || isCreatingBankAccount}
          >
            {t("actions:save")}
          </CTA>
        </ModalCTAs>
      </form>
    </Modal>
  );
};
